/**
 * ProviderPostureRow — information-dense replacement for the
 * "Provider data fresh · View provider details" binary pill.
 *
 * Rerun Reimagined RR3. Renders a single horizontal row that surfaces
 * the verified vs. unavailable provider counts, with relevance-aware
 * classification so providers whose downstream SED sections are all
 * OUTSIDE the report's PROJECTION_BY_TYPE projection (SA6) render as
 * "outside relevance" rather than triggering a yellow/red warning.
 *
 * Pure presentational. The panel computes the posture and feeds it in;
 * the only behaviour wired here is the "View →" click that jumps the
 * user to the gaps step.
 */
import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { SedSectionId } from '../../../types/siteEvidence';
import type { ApiProviderId } from '../../../config/apiProviders';
import { API_PROVIDERS } from '../../../config/apiProviders';
import { PROJECTION_BY_TYPE } from '../../../services/siteEvidence';

/**
 * Provider → SED section dispatch. One entry per managed provider in
 * API_PROVIDERS. Kept locally because the existing apiProviders config
 * is a pure auth/routing surface — adding a downstream-section list
 * there would conflate provider identity with the data-coverage map.
 * If/when a wider provider→section catalogue lands, this can become a
 * thin re-export.
 */
const PROVIDER_TO_SECTIONS: Record<ApiProviderId, SedSectionId[]> = {
  gemini: ['siteResearch'],
  'nrel-solar': ['solar'],
  'nrel-wind': ['wind'],
  eia: ['electricity'],
  airnow: ['airQuality'],
};

export interface ProviderPostureItem {
  /** Provider id as it appears in API_PROVIDERS. */
  providerId: ApiProviderId;
  /**
   * Status from the report or live SED. "verified" / "verified-from-doc"
   * fall under verified; everything else (anomalous, unavailable, partial,
   * missing) is treated as unavailable.
   */
  status: 'verified' | 'unavailable';
}

export interface ProviderPostureRowProps {
  reportType: ReportTypeId;
  items: ReadonlyArray<ProviderPostureItem>;
  onView?: () => void;
}

/**
 * Decide whether a provider's downstream SED sections all sit outside
 * the current report type's projection. When true, an "unavailable"
 * status for that provider is informationally-omitted, not "missing".
 * The pure-function shape lets callers / tests exercise the rule
 * without rendering.
 */
export function isProviderOutsideRelevance(
  providerId: ApiProviderId,
  reportType: ReportTypeId,
): boolean {
  const sections = PROVIDER_TO_SECTIONS[providerId] ?? [];
  if (sections.length === 0) return false;
  const projection = new Set(PROJECTION_BY_TYPE[reportType] ?? []);
  return sections.every(s => !projection.has(s));
}

export interface ProviderPostureSummary {
  verified: number;
  total: number;
  unavailableInRelevance: ApiProviderId[];
  unavailableOutsideRelevance: ApiProviderId[];
}

export function summariseProviderPosture(
  items: ReadonlyArray<ProviderPostureItem>,
  reportType: ReportTypeId,
): ProviderPostureSummary {
  let verified = 0;
  const unavailableInRelevance: ApiProviderId[] = [];
  const unavailableOutsideRelevance: ApiProviderId[] = [];
  for (const item of items) {
    if (item.status === 'verified') {
      verified += 1;
      continue;
    }
    if (isProviderOutsideRelevance(item.providerId, reportType)) {
      unavailableOutsideRelevance.push(item.providerId);
    } else {
      unavailableInRelevance.push(item.providerId);
    }
  }
  return {
    verified,
    total: items.length,
    unavailableInRelevance,
    unavailableOutsideRelevance,
  };
}

function providerLabelFor(id: ApiProviderId): string {
  return API_PROVIDERS[id]?.displayName ?? id;
}

export function ProviderPostureRow({ reportType, items, onView }: ProviderPostureRowProps) {
  const summary = summariseProviderPosture(items, reportType);
  const inRelevanceCount = summary.unavailableInRelevance.length;
  const outsideCount = summary.unavailableOutsideRelevance.length;

  return (
    <div
      className="flex items-center flex-wrap gap-1.5 px-3 py-2 border border-border bg-surface-1 text-[10px]"
      data-testid="rerun-provider-posture"
    >
      <span className="flex items-center gap-1.5 text-success">
        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="font-mono">
          {summary.verified}/{summary.total} verified
        </span>
      </span>
      {inRelevanceCount > 0 && (
        <>
          <span className="text-text-muted">·</span>
          <span className="font-mono text-warning" data-testid="rerun-posture-unavailable">
            {inRelevanceCount} unavailable
          </span>
          <span className="text-text-muted type-body truncate">
            (
            {summary.unavailableInRelevance
              .slice(0, 2)
              .map(providerLabelFor)
              .join(', ')}
            {inRelevanceCount > 2 ? `, +${inRelevanceCount - 2} more` : ''}
            )
          </span>
        </>
      )}
      {outsideCount > 0 && (
        <>
          <span className="text-text-muted">·</span>
          <span
            className="font-mono text-text-muted"
            data-testid="rerun-posture-outside-relevance"
          >
            {outsideCount} unavailable
          </span>
          <span className="text-text-muted type-body italic truncate">
            (
            {summary.unavailableOutsideRelevance
              .slice(0, 2)
              .map(providerLabelFor)
              .join(', ')}
            {outsideCount > 2 ? `, +${outsideCount - 2} more` : ''}
            {' '}— outside relevance)
          </span>
        </>
      )}
      {onView && (
        <button
          type="button"
          onClick={onView}
          className="ml-auto type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid="rerun-provider-posture-view"
        >
          View →
        </button>
      )}
    </div>
  );
}
